import React, { useEffect, useState } from "react";
import { BrandButton as Button, Toast, AdvancedTextEditor } from "@/components";
import useToast from "@/components/useToast";
import ConfirmModal from "@/components/ConfirmModal";
import { useAuthenticatedApi } from "@/utils/callApi";

/**
 * Reusable card for editing and previewing an admin-controlled message.
 * Handles its own API load/save and renders an editor + live preview together.
 *
 * Props:
 *   cardTitle        - Heading shown on the card
 *   description      - Explanatory text shown below the heading
 *   getEndpoint      - GET endpoint to load the current message
 *   saveEndpoint     - PUT endpoint to save the message
 *   savedMessageLabel - Label for the preview section (default: "Current Saved Message")
 */  
// Id = EmailTemplate.None,
//                                              ExpectedParams = [ "htmlMessage" ],
//                                              SubjectTemplate = "New message - Hospice UK Member Data Portal",
//                                             BodyTemplate = "Message from Hospice UK Member Data Portal: {htmlMessage}"
export default function EditableMessageCard({
  cardTitle,
  description,
  getEndpoint,
  saveEndpoint,
  saveButtonLabel,
  deleteButtonLabel,
  savedMessageLabel = "Current Saved Message"
}) {
  const { secureHelpers } = useAuthenticatedApi();
  const { toast, showToast, hideToast } = useToast();

  const [message, setMessage] = useState({ title: "", message: "" });
  const [savedMessage, setSavedMessage] = useState(null);
  const [confirmModal, setConfirmModal] = useState({
      show: false,
      title: "",
      message: "",
      onConfirm: null,
      variant: "warning"
    });

  useEffect(() => {
    loadMessage();
  }, []);

  const loadMessage = async () => {
    try {
      const data = await secureHelpers.get(getEndpoint);
      const loaded = {
        title: data.title || "",
        message: data.description || ""
      };
      setSavedMessage(loaded);
      setMessage(loaded);
    } catch (error) {
      console.error(`Failed to load message from ${getEndpoint}:`, error);
    }
  };

  // Confirmation modal state
    
   // Helper to show confirmation modal
  const showConfirm = ({ title, message, onConfirm, variant = "warning" }) => {
    return new Promise((resolve) => {
      setConfirmModal({
        show: true,
        title,
        message,
        variant,
        onConfirm: () => {
          setConfirmModal(prev => ({ ...prev, show: false }));
          resolve(true);
          if (onConfirm) onConfirm();
        }
      });
    });
  };
  
  const hideConfirm = () => {
    setConfirmModal(prev => ({ ...prev, show: false }));
  };

  const handleSave = async () => {
    try {
      await secureHelpers.put(saveEndpoint, {
        title: message.title,
        description: message.message
      });
      showToast({ variant: "success", message: `${cardTitle} saved successfully` });
      await loadMessage();
    } catch (error) {
      console.error(`Failed to save message to ${saveEndpoint}:`, error);
      showToast({ variant: "error", message: `Failed to save ${cardTitle}` });
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setMessage(prev => ({ ...prev, [name]: value }));
  };

  const handleDelete = async () => {
    showConfirm({
    title: "Delete Message",
    message: `Are you sure you want to delete the message "${savedMessage.title}"? This action cannot be undone.`,
    variant: "danger",
    onConfirm: async () => {
    try {
      await secureHelpers.delete(saveEndpoint);
      showToast({ variant: "success", message: `${cardTitle} deleted successfully` });
      setMessage({ title: "", message: "" });
      setSavedMessage(null);
    } catch (error) {
      console.error(`Failed to delete message at ${saveEndpoint}:`, error);
      showToast({ variant: "error", message: `Failed to delete ${cardTitle}` });
    }
  }
});
  };

  return (
    <>
      <div className="card p-3" style={{ border: "2px solid #adb5bd" }}>
        <h4 style={{ marginTop: 0, color: "#F16D09" }}>{cardTitle}</h4>
        {description && (
          <p className="text-muted mb-3" style={{ fontSize: "var(--font-sm)" }}>
            {description}
          </p>
        )}

        <div className="mb-3">
          <label className="form-label">Title for the message</label>
          <input
            type="text"
            name="title"
            value={message.title}
            onChange={handleChange}
            className="form-control"
            placeholder="Enter the title..."
          />
        </div>

        <div className="mb-3">
          <label className="form-label">Message</label>
          <AdvancedTextEditor
            value={message.message}
            onChange={(value) => handleChange({ target: { name: "message", value } })}
          />
        </div>

    <div style={{ display: "flex",  justifyContent: "space-between" }}>
        <Button onClick={handleSave}>
           {saveButtonLabel}
        </Button>

         <Button onClick={handleDelete}>
           {deleteButtonLabel}
        </Button>
    </div>
        {/* Preview of current saved message - lives inside the same card */}
        <div className="mt-4">
          <div className="card p-3" style={{ border: "1px solid #dee2e6", backgroundColor: "#f8f9fa" }}>
            <label style={{ color: "#2e1719", fontSize: "var(--font-lg)" }} className="form-label mb-2">
              {savedMessageLabel}
            </label>
            {savedMessage && savedMessage.title ? (
              <>
                <p className="text-muted mb-2" style={{ fontSize: "var(--font-base)" }}>
                  This is what users currently see.
                </p>
                <div style={{ backgroundColor: "#ffffff", padding: "1rem", borderRadius: "4px", border: "1px solid #dee2e6", overflowY: "auto", maxHeight: "300px" }}>
                  <h5 style={{ marginTop: 0, marginBottom: "1rem", color: "#F16D09", fontSize: "var(--font-subtitle)" }}>
                    {savedMessage.title}
                  </h5>
                  <div
                    className="mb-0"
                    style={{ fontSize: "var(--font-lg)" }}
                    dangerouslySetInnerHTML={{ __html: savedMessage.message }}
                  />
                </div>
              </>
            ) : (
              <p className="text-muted" style={{ fontSize: "var(--font-base)" }}>No message saved yet.</p>
            )}
          </div>
        </div>
      </div>
                  
                  {/* Confirmation Modal */}
                  <ConfirmModal
                    show={confirmModal.show}
                    title={confirmModal.title}
                    message={confirmModal.message}
                    onConfirm={confirmModal.onConfirm}
                    onCancel={hideConfirm}
                    variant={confirmModal.variant}
                    style={{ zIndex: 20000 }}
                  />
      {toast.show && (
        <Toast message={toast.message} variant={toast.variant} onClose={hideToast} />
      )}
    </>
  );
}
